// SPDX-License-Identifier: MIT
// Copyright contributors to the openassistant project

import { Message, generateId } from 'ai';
import { StreamMessage, StreamMessageSchema } from '../types';
import { z } from 'zod';

export type Conversation = {
  prompt: string;
  response: StreamMessage;
};

export const ConversationSchema = z.object({
  prompt: z.string(),
  response: StreamMessageSchema,
});

/**
 * Rebuild the messages from the conversations, which is an array of `{prompt, response}`.
 * The messages are in the format of the Message interface in Vercel AI SDK.
 * This function can be used to restore the messages from the persisted conversations.
 *
 * For example:
 * ```ts
 * const messages = rebuildMessages([
 *   {
 *     prompt: 'What is the capital of France?',
 *     response: { text: 'Paris' },
 *   },
 * ]);
 *
 * // create assistant
 * const assistant = createAssistant({
 *   model: 'gpt-4o',
 * });
 *
 * assistant.setMessages(messages);
 * ```
 *
 * @param conversations The conversations to rebuild the messages from
 * @returns The messages used in the Vercel AI SDK
 */
export function rebuildMessages(conversations: Conversation[]): Message[] {
  const result: Message[] = [];

  for (const msg of conversations) {
    // Handle user messages
    result.push({
      id: generateId(), // Generate random ID
      role: 'user',
      content: msg.prompt,
      parts: [
        {
          type: 'text',
          text: msg.prompt,
        },
      ],
    });

    // Handle assistant messages with tool calls
    for (const part of msg.response?.parts || []) {
      result.push({
        id: generateId(),
        role: 'assistant',
        content: '',
        parts: [part],
      });
    }
  }

  return result;
}
